import type { SimInputs } from './engine/types'
import { clampPlan, DEFAULT_PLAN, MAX_PROJECTION_AGE, toSimInputs, YEARS_PAST_TARGET } from './plan'
import type { Plan } from './plan'

/**
 * Two versions of a plan, held side by side.
 *
 * ── A scenario is not "a Plan" ──────────────────────────────────────────────
 * It is a label, a hue, and **a source of `SimInputs`**. That distinction looks
 * like ceremony today, when the only source is a `Plan` the sliders write to, and
 * it is the whole reason this file exists.
 *
 * F5 is rent-vs-buy, and a "buy" scenario is not a plan with different slider
 * values. Its down payment comes *out of* `currentCorpus`, its EMI competes with
 * `monthlyInvestment`, maintenance and property tax land in expenses, and it holds
 * an illiquid asset appreciating at a rate none of the three modelled sleeves
 * carries. Several `Plan` fields stop being typed in and start being derived.
 *
 * So everything downstream consumes `inputsFor()`, and nothing but the sliders
 * touches `scenario.plan`. When F5 adds a derived scenario, `Scenario` gains a
 * variant and `inputsFor` switches on it — the chart, the worker, the codec and
 * the delta line do not change. Widening `Plan` for everyone, or introducing a
 * second scenario shape at the call sites, is the outcome this prevents.
 */

export type ScenarioId = 'a' | 'b'

/** Both hues were validated in `tokens.css` for separation, surface contrast and
 *  colour-vision-deficiency safety. Gold is absent: it belongs to the goal. */
export const SCENARIO_HUE: Record<ScenarioId, string> = {
  a: '--c-scenario-a',
  b: '--c-scenario-b',
}

export interface Scenario {
  /**
   * Never reused, unlike `id`.
   *
   * `id` is a display slot — there is always an 'a', and dropping a comparison
   * renames the survivor to 'a' so a lone fan is never "Plan B". That makes `id`
   * unusable as a cache key: the simulation map, the animation state and the
   * worker's coalescing map are all keyed by it, and a key that comes back is
   * served the previous occupant's result. Stop comparing while editing B and
   * the kept plan inherits A's result — which the share card then renders and
   * offers to publish, carrying numbers from a plan the user just deleted.
   *
   * So identity for anything cached is this, and it is minted once per scenario
   * instance and never recycled.
   */
  uid: string
  id: ScenarioId
  /** Fixed. Renaming is out of scope for F4 — a text input in the thumb zone. */
  label: string
  /** A CSS custom property name, not a colour. Themes resolve it. */
  hueToken: string
  plan: Plan
}

export interface ScenarioSet {
  /** One or two. One is the arrival state and renders exactly today's chart. */
  scenarios: Scenario[]
  activeId: ScenarioId
}

const LABEL: Record<ScenarioId, string> = { a: 'A', b: 'B' }

/** Monotonic within a page load; a fresh load re-simulates anyway. */
let uidCounter = 0
const nextUid = (): string => `s${++uidCounter}`

export function makeScenario(id: ScenarioId, plan: Plan, uid: string = nextUid()): Scenario {
  return { uid, id, label: LABEL[id], hueToken: SCENARIO_HUE[id], plan }
}

export const DEFAULT_SCENARIO_SET: ScenarioSet = {
  scenarios: [makeScenario('a', DEFAULT_PLAN)],
  activeId: 'a',
}

export function activeScenario(set: ScenarioSet): Scenario {
  return set.scenarios.find((s) => s.id === set.activeId) ?? set.scenarios[0]
}

export function comparisonScenario(set: ScenarioSet): Scenario | null {
  if (set.scenarios.length < 2) return null
  return set.scenarios.find((s) => s.id !== set.activeId) ?? null
}

export const isComparing = (set: ScenarioSet): boolean => set.scenarios.length > 1

/**
 * One age across the set, taken from the first scenario.
 *
 * You are one age. Letting `currentAge` diverge would ragged the left edge of the
 * chart for no product reason, and would mean the age slider silently belonged to
 * only one of the two. Every other field diverges freely.
 */
export function withSharedAge(scenarios: Scenario[]): Scenario[] {
  if (scenarios.length < 2) return scenarios
  const currentAge = scenarios[0].plan.currentAge
  return scenarios.map((s) =>
    s.plan.currentAge === currentAge ? s : { ...s, plan: { ...s.plan, currentAge } },
  )
}

/**
 * One projection horizon for the whole set — **not** one per scenario.
 *
 * `toSimInputs` derives `projectToAge` from a plan's own `fireByAge`, which is
 * correct for one fan and wrong for two. Scenarios retiring at 50 and 57 would
 * project to 60 and 67, and the shorter one terminates in a hard vertical edge
 * partway across a chart whose x-domain spans both. It reads as truncated data —
 * the requirement line stops at the same age, because it is drawn from the same
 * series — and it happens under every possible encoding, which is how you can
 * tell it is not an encoding problem. Rendered before and after in
 * `docs/sketches/f4/encodings.html` §2.
 */
export function sharedHorizon(scenarios: Scenario[]): number {
  const latest = Math.max(...scenarios.map((s) => clampPlan(s.plan).fireByAge))
  return Math.min(MAX_PROJECTION_AGE, latest + YEARS_PAST_TARGET)
}

/**
 * The seam. Everything downstream of the sliders goes through here.
 *
 * Takes the horizon rather than computing it, because it is a property of the SET
 * and this function only sees one member.
 */
export function inputsFor(scenario: Scenario, horizon: number): SimInputs {
  return toSimInputs(scenario.plan, horizon)
}

/** Adds the second scenario by duplicating the active one, and focuses it.
 *  Duplicating rather than starting from defaults is the point: you compare
 *  against where you are, then change one thing. */
export function addComparison(set: ScenarioSet): ScenarioSet {
  if (set.scenarios.length > 1) return set
  const source = activeScenario(set)
  const nextId: ScenarioId = source.id === 'a' ? 'b' : 'a'
  // A fresh uid, so the duplicate cannot be served a previous occupant's result.
  const added = makeScenario(nextId, source.plan)
  const scenarios = source.id === 'a' ? [source, added] : [added, source]
  return { scenarios, activeId: nextId }
}

/** Drops the non-active scenario and returns to the single-fan chart. */
export function removeComparison(set: ScenarioSet): ScenarioSet {
  if (set.scenarios.length < 2) return set
  const kept = activeScenario(set)
  // The display slot becomes 'a'; the uid does NOT change, so the kept scenario
  // keeps its own result instead of inheriting the discarded one's.
  return { scenarios: [makeScenario('a', kept.plan, kept.uid)], activeId: 'a' }
}

export function setActive(set: ScenarioSet, activeId: ScenarioId): ScenarioSet {
  return set.scenarios.some((s) => s.id === activeId) ? { ...set, activeId } : set
}

/** Patches the active scenario's plan, keeping `currentAge` shared across both. */
export function updateActivePlan(set: ScenarioSet, patch: Partial<Plan>): ScenarioSet {
  const scenarios = set.scenarios.map((s) =>
    s.id === set.activeId ? { ...s, plan: { ...s.plan, ...patch } } : s,
  )
  // The age slider belongs to the person, not to the scenario, so it propagates.
  const shared =
    patch.currentAge === undefined
      ? scenarios
      : scenarios.map((s) => ({ ...s, plan: { ...s.plan, currentAge: patch.currentAge! } }))
  return { ...set, scenarios: shared }
}
